import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { UserTracking, BugTracking } from "@bfc-software/app-toolbox";
import { LocalStorage } from "@bfc-software/app-toolbox";

import { getEnv } from "@bfc-software/bfc-web-ui/environment";
import { mswActive } from "@bfc-software/bfc-web-ui/lib/msw";

import { routeTree } from "./routeTree.gen.ts";

const router = createRouter({ routeTree, notFoundMode: "root" });

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const currentAppVersion = getEnv().version;
const lastAppVersion = LocalStorage.getStorageItem("applicationVersion");
if (currentAppVersion !== lastAppVersion) {
  LocalStorage.clearLocalStorage();
}

LocalStorage.storeApplicationVersion(currentAppVersion);

if (mswActive()) {
  const { worker } = await import("@bfc-software/bfc-web-ui/mocks/browser");
  const env = getEnv();
  const apiOrigins = new Set(
    [
      env.tpsServiceBase,
      env.webhooksServiceBase,
      env.analyticsServiceBase,
      env.authServiceUrl,
      env.issServiceBaseUrl,
      env.orgCfgServiceBase,
      env.auditServiceBase,
      env.deliverServiceBase,
    ].map((base) => new URL(base, window.location.origin).origin),
  );
  // An API request with no handler would reach the real service, which 401s
  // the mocked session and signs the user out. Fail it loudly instead; assets,
  // Vite modules and HMR are none of our business and go through untouched.
  await worker.start({
    onUnhandledRequest: (request, print) => {
      const url = new URL(request.url);
      // The FTP base is a same-origin proxy path, so it is matched by path.
      if (
        apiOrigins.has(url.origin) ||
        url.pathname.startsWith(env.ftpServiceBaseUrl)
      ) {
        print.error();
      }
    },
  });
}

BugTracking.init(router);
UserTracking.init();

createRoot(document.getElementById("root")!, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(<RouterProvider router={router} />);
